<template>
  <div class="settings-section">
    <h2 class="section-title">{{ $t('loyalty.title', 'Loyalty Program') }}</h2>
    <p class="section-description">{{ $t('loyalty.description', 'Configure customer loyalty rewards and point redemption') }}</p>

    <div v-if="isLoading" class="loading-state">
      <n-spin size="medium" />
    </div>

    <div v-else class="settings-form">
      <!-- Enable Toggle -->
      <div class="form-field toggle-field">
        <label class="toggle-wrapper">
          <input
            type="checkbox"
            v-model="formData.is_enabled"
            class="toggle-input"
          />
          <span class="toggle-switch"></span>
          <span class="toggle-label">{{ $t('loyalty.enabled', 'Enable Loyalty Program') }}</span>
        </label>
        <p class="field-hint">{{ $t('loyalty.enabledHint', 'Allow customers to earn and redeem points') }}</p>
      </div>

      <!-- Loyalty Settings (only show when enabled) -->
      <template v-if="formData.is_enabled">
        <div class="divider"></div>

        <!-- Points Earning -->
        <div class="form-field">
          <label class="form-label">
            {{ $t('loyalty.pointsPerRupee', 'Points Per Rupee') }}
          </label>
          <div class="input-with-suffix">
            <input
              type="number"
              v-model.number="formData.points_per_rupee"
              class="form-input"
              step="0.1"
              min="0.1"
              max="10"
            />
            <span class="input-suffix">points/₹</span>
          </div>
          <p class="field-hint">
            {{ $t('loyalty.pointsPerRupeeHint', 'Number of points earned per rupee spent (e.g., 1.0 = 1 point per rupee)') }}
          </p>
        </div>

        <!-- Redemption Value -->
        <div class="form-field">
          <label class="form-label">
            {{ $t('loyalty.rupeesPerPoint', 'Rupees Per Point') }}
          </label>
          <div class="input-with-suffix">
            <input
              type="number"
              v-model.number="formData.rupees_per_point"
              class="form-input"
              step="0.1"
              min="0.1"
              max="10"
            />
            <span class="input-suffix">₹/point</span>
          </div>
          <p class="field-hint">
            {{ $t('loyalty.rupeesPerPointHint', 'Redemption value of each point (e.g., 1.0 = ₹1 per point)') }}
          </p>
        </div>

        <!-- Minimum Redeem -->
        <div class="form-field">
          <label class="form-label">
            {{ $t('loyalty.minRedeemPoints', 'Minimum Points to Redeem') }}
          </label>
          <div class="input-with-suffix">
            <input
              type="number"
              v-model.number="formData.min_redeem_points"
              class="form-input"
              step="10"
              min="10"
              max="1000"
            />
            <span class="input-suffix">points</span>
          </div>
          <p class="field-hint">
            {{ $t('loyalty.minRedeemPointsHint', 'Minimum points required before customers can redeem') }}
          </p>
        </div>

        <!-- Expiry -->
        <div class="form-field">
          <label class="form-label">
            {{ $t('loyalty.expiryMonths', 'Points Expiry') }}
          </label>
          <div class="input-with-suffix">
            <input
              type="number"
              v-model.number="formData.expiry_months"
              class="form-input"
              step="1"
              min="1"
              max="120"
            />
            <span class="input-suffix">months</span>
          </div>
          <p class="field-hint">
            {{ $t('loyalty.expiryMonthsHint', 'Points will expire after this many months of inactivity (0 = never expire)') }}
          </p>
        </div>

        <!-- Max Discount -->
        <div class="form-field">
          <label class="form-label">
            {{ $t('loyalty.maxDiscount', 'Maximum Discount') }}
          </label>
          <div class="input-with-suffix">
            <input
              type="number"
              v-model.number="formData.max_discount_percent"
              class="form-input"
              step="5"
              min="0"
              max="100"
            />
            <span class="input-suffix">%</span>
          </div>
          <p class="field-hint">
            {{ $t('loyalty.maxDiscountHint', 'Maximum bill percentage payable with points (0 = no limit)') }}
          </p>
        </div>

        <!-- Preview Card -->
        <div class="preview-card">
          <div class="preview-header">
            <span class="preview-icon">✨</span>
            <span class="preview-title">{{ $t('loyalty.preview', 'How it works') }}</span>
          </div>
          <div class="preview-content">
            <div class="preview-item">
              <span class="preview-label">{{ $t('loyalty.earning', 'Earning') }}</span>
              <span class="preview-value">₹100 purchase = {{ (formData.points_per_rupee * 100).toFixed(0) }} points</span>
            </div>
            <div class="preview-item">
              <span class="preview-label">{{ $t('loyalty.redemption', 'Redemption') }}</span>
              <span class="preview-value">{{ formData.min_redeem_points }} points = ₹{{ (formData.min_redeem_points * formData.rupees_per_point).toFixed(0) }} discount</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Save Footer -->
    <div class="save-footer" v-if="isDirty || saveStatus">
      <button
        v-if="isDirty"
        class="btn-discard"
        @click="handleDiscard"
      >
        {{ $t('settings.discard', 'Discard') }}
      </button>
      <IntercomButton
        variant="primary"
        :class="{ saved: saveStatus === 'saved' }"
        :disabled="!isDirty && saveStatus !== 'saved'"
        @click="handleSave"
      >
        {{ isSaving ? $t('common.saving', 'Saving...') : saveStatus === 'saved' ? $t('common.saved', 'Saved') + ' ✓' : $t('settings.saveChanges', 'Save Changes') }}
      </IntercomButton>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSpin } from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useBilling } from '../../composables/useBilling.js'
import { IntercomButton } from '@shared/components/intercom'

const { t } = useI18n()
const toast = useToast()
const { getLoyaltySettings, updateLoyaltySettings } = useBilling()

const originalData = ref({})
const isLoading = ref(true)
const isSaving = ref(false)
const saveStatus = ref(null)

const formData = reactive({
  is_enabled: false,
  points_per_rupee: 1.0,
  rupees_per_point: 1.0,
  min_redeem_points: 100,
  expiry_months: 12,
  max_discount_percent: 0,
})

// Track dirty state
const isDirty = computed(() => {
  return JSON.stringify(formData) !== JSON.stringify(originalData.value)
})

const handleSave = async () => {
  if (!isDirty.value) return

  // Validate
  if (formData.is_enabled) {
    if (formData.points_per_rupee <= 0) {
      toast.warning(t('loyalty.invalidPointsPerRupee', 'Points per rupee must be greater than 0'))
      return
    }
    if (formData.rupees_per_point <= 0) {
      toast.warning(t('loyalty.invalidRupeesPerPoint', 'Rupees per point must be greater than 0'))
      return
    }
    if (formData.min_redeem_points < 10) {
      toast.warning(t('loyalty.invalidMinRedeem', 'Minimum redeem points must be at least 10'))
      return
    }
  }

  isSaving.value = true
  try {
    const data = {
      is_enabled: formData.is_enabled,
      points_per_rupee: formData.points_per_rupee,
      rupees_per_point: formData.rupees_per_point,
      min_redeem_points: formData.min_redeem_points,
      expiry_months: formData.expiry_months,
      max_discount_percent: formData.max_discount_percent,
    }
    await updateLoyaltySettings(data)
    // Update original data to match current
    originalData.value = { ...formData }
    saveStatus.value = 'saved'
    toast.success(t('loyalty.updated', 'Loyalty settings updated successfully'))
    setTimeout(() => {
      saveStatus.value = null
    }, 2000)
  } catch (error) {
    console.error('Failed to save loyalty settings:', error)
    toast.error(t('loyalty.updateFailed', 'Failed to update loyalty settings'))
  } finally {
    isSaving.value = false
  }
}

const handleDiscard = () => {
  Object.assign(formData, originalData.value)
}

const fetchLoyaltySettings = async () => {
  isLoading.value = true
  try {
    const response = await getLoyaltySettings()
    // API returns { settings: { is_enabled, ... } } - unwrap the nested object
    const settings = response.settings

    // Populate form with loyalty data
    formData.is_enabled = settings.is_enabled || false
    formData.points_per_rupee = settings.points_per_rupee || 1.0
    formData.rupees_per_point = settings.rupees_per_point || 1.0
    formData.min_redeem_points = settings.min_redeem_points || 100
    formData.expiry_months = settings.expiry_months || 12
    formData.max_discount_percent = settings.max_discount_percent || 0

    // Store original for dirty checking
    originalData.value = { ...formData }
  } catch (error) {
    console.log('No loyalty settings found, using defaults')
    formData.is_enabled = false
    formData.points_per_rupee = 1.0
    formData.rupees_per_point = 1.0
    formData.min_redeem_points = 100
    formData.expiry_months = 12
    formData.max_discount_percent = 0
    originalData.value = { ...formData }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchLoyaltySettings()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.settings-section {
  padding: var(--space-6);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-1) 0;
}

.section-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-6) 0;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-8);
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin: 0;
}

.divider {
  height: 1px;
  background: var(--color-gray-200);
  margin: var(--space-2) 0;
}

/* Toggle */
.toggle-field {
  gap: var(--space-3);
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
}

.toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch {
  position: relative;
  width: 48px;
  height: 28px;
  background: var(--color-gray-300);
  border-radius: 14px;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.toggle-switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 24px;
  height: 24px;
  background: var(--color-white);
  border-radius: 50%;
  transition: all var(--transition-fast);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-input:checked + .toggle-switch {
  background: var(--color-success);
}

.toggle-input:checked + .toggle-switch::after {
  left: 22px;
}

.toggle-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

/* Input with suffix */
.input-with-suffix {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.form-input {
  flex: 1;
  max-width: 150px;
  height: 44px;
  padding: 0 var(--space-3);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  outline: none;
  transition: all var(--transition-fast);
}

.form-input:focus {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.input-suffix {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  white-space: nowrap;
}

/* Preview Card */
.preview-card {
  background: linear-gradient(135deg, var(--color-blue-light) 0%, #e8f4fd 100%);
  border: 1px solid var(--color-blue);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
}

.preview-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.preview-icon {
  font-size: var(--font-size-lg);
}

.preview-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-blue-dark);
}

.preview-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.preview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
}

.preview-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

/* Save Footer */
.save-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  margin: var(--space-6) calc(-1 * var(--space-6)) calc(-1 * var(--space-6));
  background: var(--color-white);
  border-top: 1px solid var(--color-gray-200);
}

.btn-discard {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-discard:hover {
  background: var(--color-gray-50);
  border-color: var(--color-gray-400);
}

.saved {
  background: var(--color-success) !important;
}
</style>
